const toMillis = (time) => (time instanceof Date ? time.getTime() : Number(time));
const mean = (values) => values.reduce((sum, v) => sum + v, 0) / values.length;
const isValidNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * @function computeErrorMetrics
 * @description Computes the global error metrics (whole-simulation, last-6-months and
 * final-date RMSE/bias) from the merged modeled × reference rows. Rows
 * missing either distance are ignored. Throws on empty input or when no
 * valid pair remains.
 * @param {Array<{transectId, time, distanceModeled, timeReference, distanceReference}>} mergedDistances
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {{
 *   rowsWithError: Array<{transectId, time, distanceModeled, timeReference, distanceReference, error, squaredError}>,
 *   metrics: {rmseAvg, biasAvg, rmseLast6m, rmseFinal, biasFinal}
 * }}
 */
export const computeErrorMetrics = (mergedDistances) => {
  if (!mergedDistances || !mergedDistances.length) {
    throw new Error('mergedDistances is empty.');
  }

  const rowsWithError = mergedDistances
    .filter((r) => isValidNumber(r.distanceReference) && isValidNumber(r.distanceModeled))
    .map((r) => {
      const error = r.distanceModeled - r.distanceReference;
      return { ...r, error, squaredError: error * error };
    });

  if (!rowsWithError.length) {
    throw new Error('No valid paired data after dropping rows without a matched reference distance.');
  }

  const rmseAvg = Math.sqrt(mean(rowsWithError.map((r) => r.squaredError)));
  const biasAvg = mean(rowsWithError.map((r) => r.error));

  const lastObsTime = rowsWithError.reduce(
    (max, r) => (toMillis(r.timeReference) > toMillis(max) ? r.timeReference : max),
    rowsWithError[0].timeReference,
  );
  const sixMonthsBefore = new Date(lastObsTime);
  sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6);

  const rowsLast6m = rowsWithError.filter((r) => toMillis(r.timeReference) >= sixMonthsBefore.getTime());
  const rmseLast6m = rowsLast6m.length ? Math.sqrt(mean(rowsLast6m.map((r) => r.squaredError))) : NaN;

  const rowsFinal = rowsWithError.filter((r) => toMillis(r.timeReference) === toMillis(lastObsTime));
  const rmseFinal = rowsFinal.length ? Math.sqrt(mean(rowsFinal.map((r) => r.squaredError))) : NaN;
  const biasFinal = rowsFinal.length ? mean(rowsFinal.map((r) => r.error)) : NaN;

  return {
    rowsWithError,
    metrics: { rmseAvg, biasAvg, rmseLast6m, rmseFinal, biasFinal },
  };
};

/**
 * @function computeAggregatedErrorMetrics
 * @description Aggregates the per-row errors into per-date and per-transect bias/RMS,
 * used to feed the validation plots (RMS/bias over time, RMS/bias per
 * transect).
 * @param {Array<{transectId, time, error, squaredError}>} rowsWithError
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {{byDate: Array<{time, bias, rms}>, byTransect: Array<{transectId, bias, rms}>}}
 */
export const computeAggregatedErrorMetrics = (rowsWithError) => {
  const byDateMap = new Map();
  const byTransectMap = new Map();

  rowsWithError.filter((r) => isValidNumber(r.error)).forEach((row) => {
    const dateKey = toMillis(row.time);
    if (!byDateMap.has(dateKey)) byDateMap.set(dateKey, { time: row.time, rows: [] });
    byDateMap.get(dateKey).rows.push(row);

    if (!byTransectMap.has(row.transectId)) byTransectMap.set(row.transectId, { transectId: row.transectId, rows: [] });
    byTransectMap.get(row.transectId).rows.push(row);
  });

  const summarize = (rows) => ({
    bias: mean(rows.map((r) => r.error)),
    rms: Math.sqrt(mean(rows.map((r) => r.squaredError))),
  });

  const byDate = Array.from(byDateMap.values())
    .sort((a, b) => toMillis(a.time) - toMillis(b.time))
    .map(({ time, rows }) => ({ time, ...summarize(rows) }));

  const byTransect = Array.from(byTransectMap.values())
    .sort((a, b) => (a.transectId > b.transectId ? 1 : -1))
    .map(({ transectId, rows }) => ({ transectId, ...summarize(rows) }));

  return { byDate, byTransect };
};

/**
 * @function computeMeasuredDeviation
 * @description Measured deviation of the coastline over the simulated period: per
 * transect, the difference between the reference distance at the last
 * successfully paired date and the initial modeled coastline's distance
 * Summarized as biasm` (mean error) and `rmsm` (root-mean-square error); both are NaN
 * when no pair can be formed.
 * @param {Array<{transectId, time, distance}>} modeledDistances
 * @param {Array<{transectId, time, distance}>} referenceDistances
 * @param {Array<{transectId, timeReference, error}>} rowsWithError - `computeErrorMetrics` output.
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {{
 *   rows: Array<{transectId, distanceInitial, distanceFinal, error, squaredError}>,
 *   biasm: number,
 *   rmsm: number,
 *   initialTime: (Date|null),
 *   lastUsefulTime: (Date|null)
 * }}
 */
export const computeMeasuredDeviation = (modeledDistances, referenceDistances, rowsWithError) => {
  const empty = { rows: [], biasm: NaN, rmsm: NaN, initialTime: null, lastUsefulTime: null };

  const validModeled = (modeledDistances || []).filter((r) => isValidNumber(r.distance));
  if (!validModeled.length || !rowsWithError || !rowsWithError.length) return empty;

  const initialMillis = Math.min(...validModeled.map((r) => toMillis(r.time)));
  const initialByTransect = new Map(
    validModeled.filter((r) => toMillis(r.time) === initialMillis).map((r) => [r.transectId, r]),
  );

  const lastUsefulMillis = Math.max(...rowsWithError.map((r) => toMillis(r.timeReference)));

  const rows = (referenceDistances || [])
    .filter((r) => toMillis(r.time) === lastUsefulMillis && isValidNumber(r.distance))
    .filter((r) => initialByTransect.has(r.transectId))
    .map((r) => {
      const distanceInitial = initialByTransect.get(r.transectId).distance;
      const error = r.distance - distanceInitial;
      return { transectId: r.transectId, distanceInitial, distanceFinal: r.distance, error, squaredError: error * error };
    });

  if (!rows.length) return empty;

  return {
    rows,
    biasm: mean(rows.map((r) => r.error)),
    rmsm: Math.sqrt(mean(rows.map((r) => r.squaredError))),
    initialTime: new Date(initialMillis),
    lastUsefulTime: new Date(lastUsefulMillis),
  };
};

/**
 * @function computeBrierSkillScore
 * @description Brier Skill Score of the latest modeled coastline: `1 - rmseFinal / rmsm`,
 * comparing the model's final-date error against the measured deviation of
 * the coastline itself.
 * @param {number} rmseFinal
 * @param {number} rmsm
 * **Called by:
 * @see {shoreSAnalysis/saga}
 * @memberof module:ShoreS
 * @returns {number} NaN when either input is invalid or `rmsm` is 0.
 */
export const computeBrierSkillScore = (rmseFinal, rmsm) =>
  isValidNumber(rmseFinal) && isValidNumber(rmsm) && rmsm !== 0 ? 1 - rmseFinal / rmsm : NaN;
